import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, ExternalLink, Calendar, User, Share2, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ZapButton } from '@/components/ZapButton';
import { AppSupporters } from '@/components/AppSupporters';
import { useApp } from '@/hooks/useApps';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import { nip19 } from 'nostr-tools';
import NotFound from './NotFound';

interface AppDetailsPageProps {
  naddr: {
    kind: number;
    pubkey: string;
    identifier: string;
  };
}

export default function AppDetailsPage({ naddr }: AppDetailsPageProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  
  const { data: app, isLoading } = useApp(naddr.pubkey, naddr.identifier);
  const poster = useAuthor(naddr.pubkey);
  const creator = useAuthor(app?.creator || naddr.pubkey);
  const posterMetadata = poster.data?.metadata;
  const creatorMetadata = creator.data?.metadata;

  const creatorDisplayName = creatorMetadata?.name || creatorMetadata?.display_name || genUserName(app?.creator || naddr.pubkey);
  const posterDisplayName = posterMetadata?.name || posterMetadata?.display_name || genUserName(naddr.pubkey);
  const creatorAvatar = creatorMetadata?.picture;
  const posterAvatar = posterMetadata?.picture;
  const isOwner = user?.pubkey === naddr.pubkey;
  
  // Get current URL dynamically for OG tags
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://nostrstore.com';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const appUrl = `${currentOrigin}${currentPath}`;
  const ogImage = app?.image || `${currentOrigin}/og-image.jpg`;

  useSeoMeta({
    title: app ? `${app.name} - NostrStore.com` : 'Loading...',
    description: app?.description || 'Discover decentralized apps on Nostr',
    ogTitle: app ? `${app.name} - NostrStore.com` : 'NostrStore.com',
    ogDescription: app?.description || 'Discover decentralized apps on Nostr',
    ogImage: ogImage,
    ogUrl: appUrl,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: app ? `${app.name} - NostrStore.com` : 'NostrStore.com',
    twitterDescription: app?.description || 'Discover decentralized apps on Nostr',
    twitterImage: ogImage,
  });

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: app?.name,
        text: app?.description,
        url,
      }).catch(() => {
        // Fallback to clipboard
        navigator.clipboard.writeText(url);
        toast({
          title: 'Link Copied',
          description: 'App link copied to clipboard',
        });
      });
    } else {
      navigator.clipboard.writeText(url);
      toast({
        title: 'Link Copied',
        description: 'App link copied to clipboard',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black/95 relative px-4 py-8">
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-800/10 via-purple-900/5 to-transparent opacity-50" />
        </div>

        <div className="relative max-w-5xl mx-auto space-y-6">
          <Skeleton className="h-10 w-32" />
          <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20">
            <Skeleton className="w-full h-96 rounded-t-xl" />
            <CardContent className="p-8 space-y-6">
              <div className="flex items-start gap-4">
                <Skeleton className="w-24 h-24 rounded-xl" />
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-8 w-64" />
                  <Skeleton className="h-5 w-32" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!app) {
    return <NotFound />;
  }

  const formattedDate = new Date(app.createdAt * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-black/95 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-800/10 via-purple-900/5 to-transparent opacity-50" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-[40rem] h-[40rem] bg-purple-600/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '20s' }} />
          <div className="absolute top-[20%] -right-4 w-[35rem] h-[35rem] bg-purple-800/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '25s', animationDelay: '2s' }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-purple-200 hover:text-purple-100 hover:bg-purple-500/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Apps
        </Button>

        {/* App Card */}
        <Card className="overflow-hidden bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
          {/* App Image */}
          {app.image && (
            <div className="relative w-full h-64 sm:h-96 overflow-hidden isolate">
              <img
                src={app.image}
                alt={app.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent -z-10" />
              
              {/* Category Badge on Image */}
              <div className="absolute top-4 right-4">
                <Badge className="bg-purple-600/80 text-white border-purple-500/50 backdrop-blur-sm text-sm px-3 py-1">
                  {app.category}
                </Badge>
              </div>
            </div>
          )}

          <CardContent className="p-6 sm:p-8 space-y-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Logo */}
              {app.logo ? (
                <img
                  src={app.logo}
                  alt={`${app.name} logo`}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shadow-xl ring-2 ring-purple-500/30"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-900/20 flex items-center justify-center ring-2 ring-purple-500/30 shadow-xl">
                  <span className="text-4xl sm:text-5xl">📱</span>
                </div>
              )}

              {/* Title and Meta */}
              <div className="flex-1 min-w-0 space-y-3">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-purple-50 mb-2 bg-gradient-to-r from-purple-200 to-purple-100 bg-clip-text text-transparent">
                    {app.name}
                  </h1>
                  <div className="flex gap-2 flex-wrap">
                    {!app.image && (
                      <Badge className="bg-purple-500/20 text-purple-200/90 border-purple-500/30">
                        {app.category}
                      </Badge>
                    )}
                    {!app.isPostedByCreator && (
                      <Badge variant="destructive" className="bg-orange-600/80 border-orange-500/50 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Not Posted by Creator
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Warning Alert */}
                {!app.isPostedByCreator && (
                  <Alert className="bg-orange-500/10 border-orange-500/30 text-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-400" />
                    <AlertDescription className="text-sm">
                      This app was posted by <strong>{posterDisplayName}</strong> on behalf of the creator. Verify authenticity before use.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Creator Info */}
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/npub${nip19.npubEncode(app.creator || app.author).slice(4)}`)}
                    className="flex items-center gap-2 hover:bg-purple-500/10 px-3 py-2 rounded-lg transition-colors group"
                  >
                    <Avatar className="h-8 w-8 ring-2 ring-purple-500/20">
                      <AvatarImage src={creatorAvatar} alt={creatorDisplayName} />
                      <AvatarFallback className="bg-purple-900/30 text-purple-300 text-sm">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <div className="text-sm font-medium text-purple-200 group-hover:text-purple-100">
                        {creatorDisplayName}
                      </div>
                      <div className="text-xs text-purple-300/50">
                        Creator
                      </div>
                    </div>
                  </button>

                  {!app.isPostedByCreator && (
                    <button
                      onClick={() => navigate(`/npub${nip19.npubEncode(app.author).slice(4)}`)}
                      className="flex items-center gap-2 hover:bg-purple-500/10 px-3 py-2 rounded-lg transition-colors group"
                    >
                      <Avatar className="h-8 w-8 ring-2 ring-purple-500/20">
                        <AvatarImage src={posterAvatar} alt={posterDisplayName} />
                        <AvatarFallback className="bg-purple-900/30 text-purple-300 text-sm">
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <div className="text-sm font-medium text-purple-200 group-hover:text-purple-100">
                          {posterDisplayName}
                        </div>
                        <div className="text-xs text-purple-300/50 flex items-center gap-1">
                          Posted {formattedDate}
                        </div>
                      </div>
                    </button>
                  )}

                  {app.isPostedByCreator && (
                    <div className="text-xs text-purple-300/50 flex items-center gap-1 px-3">
                      <Calendar className="h-3 w-3" />
                      {formattedDate}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <a
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-purple-50 font-medium transition-all shadow-lg shadow-purple-500/25"
                  >
                    Open App
                    <ExternalLink className="h-4 w-4" />
                  </a>

                  <ZapButton event={app.event} className="" />

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShare}
                    className="border-purple-500/20 text-purple-200 hover:bg-purple-500/10 hover:text-purple-100"
                  >
                    <Share2 className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>

                  {isOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-500/20 text-purple-200 hover:bg-purple-500/10 hover:text-purple-100"
                      disabled
                    >
                      <Edit className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="pt-6 border-t border-purple-500/10">
              <h2 className="text-lg font-semibold text-purple-100 mb-3">About</h2>
              <p className="text-purple-200/80 leading-relaxed whitespace-pre-wrap">
                {app.description}
              </p>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-purple-500/10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-purple-300/70 mb-1">App URL</h3>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-200 hover:text-purple-100 underline break-all text-sm"
                >
                  {app.url}
                </a>
              </div>
              <div>
                <h3 className="text-sm font-medium text-purple-300/70 mb-1">Category</h3>
                <p className="text-purple-200 text-sm">{app.category}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supporters Section */}
        <div className="mt-6">
          <AppSupporters eventId={app.event.id} />
        </div>
      </div>
    </div>
  );
}
